/**
 * "Download N missing" — the in-place fix for staged templates whose mock-up is not on this
 * device, on the Select tab and before Render. It force-syncs exactly those templates, so the
 * operator never has to go back to Templates and start the selection over.
 */
import { useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { hydrationFailure, useHydrationStore } from "../stores/hydrationStore.js";
import { findMissingMockups, forceSyncTemplate, type MockupTarget } from "../templates/forceSync.js";
import "./mockups.css";

const NAMES_SHOWN = 5;

/** The staged targets the queue would refuse, re-checked whenever a download settles. */
export function useMissingMockups<T extends MockupTarget>(targets: readonly T[]): T[] {
  const version = useHydrationStore((s) => s.version);
  const [missing, setMissing] = useState<T[]>([]);
  const key = targets.map((t) => `${t.versionId}:${t.views.join(",")}:${t.bakeExpected}`).join("|");
  useEffect(() => {
    let cancelled = false;
    void findMissingMockups(targets).then((m) => { if (!cancelled) setMissing(m); });
    return () => { cancelled = true; };
    // Keyed by content: callers pass a fresh array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);
  return missing;
}

function names(targets: readonly MockupTarget[]): string {
  const shown = targets.slice(0, NAMES_SHOWN).map((t) => `“${t.name}”`).join(", ");
  const rest = targets.length - NAMES_SHOWN;
  return rest > 0 ? `${shown} +${rest} more` : shown;
}

function missingSentence(missing: readonly MockupTarget[]): string {
  if (missing.length > 1) return `${missing.length} templates have no mock-up on this device: ${names(missing)}.`;
  const failure = hydrationFailure(missing[0]!.versionId);
  return failure
    ? `“${missing[0]!.name}” could not download its mock-up on this device (${failure.message}).`
    : `“${missing[0]!.name}” has no mock-up on this device.`;
}

export function MissingMockups({ missing, testId }: { missing: readonly MockupTarget[]; testId: string }) {
  const { io, client, logger } = useServices();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState<ReadonlyMap<string, string>>(() => new Map());
  if (missing.length === 0) return null;

  const download = async () => {
    const next = new Map<string, string>();
    setFailed(next);
    setProgress({ done: 0, total: missing.length });
    for (const t of missing) {
      try {
        const { hasBake } = await forceSyncTemplate(io, client, t.templateId);
        if (!hasBake) next.set(t.templateId, "it has no published mock-up — re-bake it in the template editor");
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        next.set(t.templateId, message);
        logger?.log("error", `Mockups · Download missing could not finish "${t.name}"`, {
          templateId: t.templateId, error: message,
        });
      }
      setProgress((p) => (p ? { done: p.done + 1, total: p.total } : p));
    }
    setFailed(new Map(next));
    setProgress(null);
  };

  const stillFailed = missing.filter((t) => failed.has(t.templateId));
  return (
    <div className="banner banner--warn missing-mockups" role="status" data-testid={testId}>
      <span>
        {missingSentence(missing)} Running now would export the designs on a bare product.
        {stillFailed.map((t) => ` “${t.name}” could not be downloaded: ${failed.get(t.templateId)}.`).join("")}
      </span>
      <button type="button" className="btn btn--sm" onClick={() => void download()}
              disabled={progress !== null} aria-busy={progress !== null} data-testid="download-missing">
        {progress !== null && <span className="btn-spinner" aria-hidden="true" />}
        Download {missing.length} missing
      </button>
      {progress && (
        <span className="faint" data-testid="download-missing-progress">
          Downloading {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
        </span>
      )}
    </div>
  );
}
